import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Plus, Search, Trash2, Pencil, Package, ShoppingCart, ClipboardList, ChevronDown, ChevronRight, AlertTriangle, CheckCircle2, Download, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { z } from "zod";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/authContext";

// ─── Brand colours ─────────────────────────────────────────────────────
const BRAND_GREEN = "#2d8653";
const BRAND_ORANGE = "#e76f00";
const BRAND_DARK = "#1a1a1a";

// ─── Constants ─────────────────────────────────────────────────────────
const CATEGORIES = ["Food", "Beverage", "Dry Goods", "Cleaning", "Paper"] as const;


const UNITS_OF_MEASURE = ["kg", "lbs", "litres", "gallons", "bottles", "cases", "packs", "units", "boxes"] as const;

interface InventoryItem {
  id: string;
  item_code: string;
  item_name: string;
  category: string;
  unit_of_measure: string;
  par_level: number;
  current_stock: number;
  supplier_name: string | null;
  unit_cost: number;
  notes: string | null;
  updated_at: string;
}

// ─── Validation ────────────────────────────────────────────────────────
const itemSchema = z.object({
  item_code: z.string().trim().min(1, "Item code is required").max(40, "Item code too long"),
  item_name: z.string().trim().min(1, "Item name is required").max(120, "Item name too long"),
  category: z.string().min(1, "Category required"),
  unit_of_measure: z.string().min(1, "Unit required"),
  par_level: z.number().min(0, "Must be ≥ 0").max(1_000_000),
  current_stock: z.number().min(0, "Must be ≥ 0").max(1_000_000),
  supplier_name: z.string().trim().max(120).optional().or(z.literal("")),
  unit_cost: z.number().min(0).max(1_000_000),
});

// ─── Helpers ───────────────────────────────────────────────────────────
function getStatus(current: number, par: number): "ok" | "low" | "critical" {
  if (par <= 0) return "ok";
  const ratio = current / par;
  if (ratio < 0.25) return "critical";
  if (ratio < 1) return "low";
  return "ok";
}

function StatusPill({ status }: { status: "ok" | "low" | "critical" }) {
  const map = {
    ok: { label: "OK", color: BRAND_GREEN, bg: `${BRAND_GREEN}1a` },
    low: { label: "Low", color: BRAND_ORANGE, bg: `${BRAND_ORANGE}1a` },
    critical: { label: "Critical", color: "#dc2626", bg: "#dc26261a" },
  } as const;
  const c = map[status];
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium"
      style={{ color: c.color, backgroundColor: c.bg }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: c.color }} />
      {c.label}
    </span>
  );
}

function fmtMoney(n: number) {
  return `$${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// ─── Add / Edit dialog ─────────────────────────────────────────────────
interface ItemFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initial?: InventoryItem | null;
  onSaved: () => void;
}

function ItemFormDialog({ open, onOpenChange, initial, onSaved }: ItemFormProps) {
  const { user } = useAuth();
  const isEdit = !!initial;
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    item_code: "",
    item_name: "",
    category: "Food" as string,
    unit_of_measure: "kg" as string,
    par_level: "0",
    current_stock: "0",
    supplier_name: "",
    unit_cost: "0",
  });

  useEffect(() => {
    if (open) {
      setForm({
        item_code: initial?.item_code ?? "",
        item_name: initial?.item_name ?? "",
        category: initial?.category ?? "Food",
        unit_of_measure: initial?.unit_of_measure ?? "kg",
        par_level: String(initial?.par_level ?? 0),
        current_stock: String(initial?.current_stock ?? 0),
        supplier_name: initial?.supplier_name ?? "",
        unit_cost: String(initial?.unit_cost ?? 0),
      });
    }
  }, [open, initial]);

  const handleSave = async () => {
    if (!user) return;
    const parsed = itemSchema.safeParse({
      item_code: form.item_code,
      item_name: form.item_name,
      category: form.category,
      unit_of_measure: form.unit_of_measure,
      par_level: Number(form.par_level) || 0,
      current_stock: Number(form.current_stock) || 0,
      supplier_name: form.supplier_name,
      unit_cost: Number(form.unit_cost) || 0,
    });
    if (!parsed.success) {
      toast.error(parsed.error.issues[0]?.message ?? "Invalid input");
      return;
    }
    setSaving(true);
    const d = parsed.data;
    const payload = {
      user_id: user.id,
      item_code: d.item_code,
      item_name: d.item_name,
      category: d.category,
      unit_of_measure: d.unit_of_measure,
      par_level: d.par_level,
      current_stock: d.current_stock,
      unit_cost: d.unit_cost,
      supplier_name: d.supplier_name ? d.supplier_name : null,
    };
    const { error } = isEdit
      ? await supabase.from("inventory_items").update(payload).eq("id", initial!.id)
      : await supabase.from("inventory_items").insert(payload);
    setSaving(false);
    if (error) {
      if (error.code === "23505") toast.error("Item code already exists. Use a unique code.");
      else toast.error(error.message);
      return;
    }
    toast.success(isEdit ? "Item updated" : "Item added");
    onOpenChange(false);
    onSaved();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading">{isEdit ? "Edit item" : "Add new item"}</DialogTitle>
          <DialogDescription>
            {isEdit ? "Update this catalog item." : "Add a new item to your inventory catalog."}
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 py-2">
          <div className="space-y-1.5">
            <Label htmlFor="code">Item Code</Label>
            <Input id="code" placeholder="FB-001" value={form.item_code} onChange={(e) => setForm((s) => ({ ...s, item_code: e.target.value.toUpperCase() }))} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="name">Item Name</Label>
            <Input id="name" placeholder="Chicken Breast" value={form.item_name} onChange={(e) => setForm((s) => ({ ...s, item_name: e.target.value }))} />
          </div>
          <div className="space-y-1.5">
            <Label>Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm((s) => ({ ...s, category: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Unit of Measure</Label>
            <Select value={form.unit_of_measure} onValueChange={(v) => setForm((s) => ({ ...s, unit_of_measure: v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {UNITS_OF_MEASURE.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label>Par Level</Label>
            <Input type="number" min={0} step="0.01" value={form.par_level} onChange={(e) => setForm((s) => ({ ...s, par_level: e.target.value }))} />
          </div>
          <div className="space-y-1.5">
            <Label>Current Stock</Label>
            <Input type="number" min={0} step="0.01" value={form.current_stock} onChange={(e) => setForm((s) => ({ ...s, current_stock: e.target.value }))} />
          </div>
          <div className="space-y-1.5 col-span-2">
            <Label>Supplier</Label>
            <Input placeholder="Sysco" value={form.supplier_name} onChange={(e) => setForm((s) => ({ ...s, supplier_name: e.target.value }))} />
          </div>
          <div className="space-y-1.5 col-span-2">
            <Label>Unit Cost (USD)</Label>
            <Input type="number" min={0} step="0.01" value={form.unit_cost} onChange={(e) => setForm((s) => ({ ...s, unit_cost: e.target.value }))} />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} style={{ backgroundColor: BRAND_GREEN }} className="text-white hover:brightness-110 border-0">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : isEdit ? "Save changes" : "Add item"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────
export default function Inventory() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [items, setItems] = useState<InventoryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterCategory, setFilterCategory] = useState<string>("all");
  const [collapsed, setCollapsed] = useState<Record<string, boolean>>({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<InventoryItem | null>(null);
  const [savingCount, setSavingCount] = useState<string | null>(null);

  const refresh = async () => {
    if (!user) return;
    setLoading(true);
    const { data, error } = await supabase
      .from("inventory_items")
      .select("*")
      .eq("user_id", user.id)
      .order("category", { ascending: true })
      .order("item_code", { ascending: true });
    if (error) toast.error(error.message);
    else setItems((data ?? []) as InventoryItem[]);
    setLoading(false);
  };

  useEffect(() => { refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [user]);

  // ─── Derived ─────────────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((it) => {
      if (filterCategory !== "all" && it.category !== filterCategory) return false;
      if (!q) return true;
      return (
        it.item_code.toLowerCase().includes(q) ||
        it.item_name.toLowerCase().includes(q) ||
        (it.supplier_name ?? "").toLowerCase().includes(q)
      );
    });
  }, [items, search, filterCategory]);

  const grouped = useMemo(() => {
    const out = new Map<string, InventoryItem[]>();
    CATEGORIES.forEach((c) => out.set(c, []));
    filtered.forEach((it) => {
      const list = out.get(it.category) ?? [];
      list.push(it);
      out.set(it.category, list);
    });
    return Array.from(out.entries()).filter(([, list]) => list.length > 0);
  }, [filtered]);

  const purchaseOrder = useMemo(() => {
    const rows = items
      .filter((it) => it.par_level > 0 && it.current_stock < it.par_level)
      .map((it) => {
        const qty = Math.max(0, it.par_level - it.current_stock);
        return {
          id: it.id,
          item_code: it.item_code,
          item_name: it.item_name,
          unit_of_measure: it.unit_of_measure,
          supplier_name: it.supplier_name,
          quantity: qty,
          unit_cost: Number(it.unit_cost),
          total: qty * Number(it.unit_cost),
          status: getStatus(it.current_stock, it.par_level),
        };
      })
      .sort((a, b) => (a.status === "critical" ? -1 : b.status === "critical" ? 1 : 0));
    const grandTotal = rows.reduce((s, r) => s + r.total, 0);
    return { rows, grandTotal };
  }, [items]);

  const stats = useMemo(() => {
    const total = items.length;
    const low = items.filter((it) => getStatus(it.current_stock, it.par_level) === "low").length;
    const critical = items.filter((it) => getStatus(it.current_stock, it.par_level) === "critical").length;
    const inventoryValue = items.reduce((s, it) => s + Number(it.current_stock) * Number(it.unit_cost), 0);
    return { total, low, critical, inventoryValue };
  }, [items]);

  // ─── Stocktake update ────────────────────────────────────────────────
  const handleStockChange = async (id: string, value: string) => {
    const n = Number(value);
    if (Number.isNaN(n) || n < 0) return;
    setSavingCount(id);
    setItems((prev) => prev.map((it) => (it.id === id ? { ...it, current_stock: n } : it)));
    const { error } = await supabase.from("inventory_items").update({ current_stock: n }).eq("id", id);
    setSavingCount(null);
    if (error) {
      toast.error("Failed to save count");
      refresh();
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this item?")) return;
    const { error } = await supabase.from("inventory_items").delete().eq("id", id);
    if (error) toast.error(error.message);
    else { toast.success("Item deleted"); refresh(); }
  };

  const exportPurchaseOrderCSV = () => {
    const header = ["Item Code", "Item Name", "Quantity", "Unit", "Supplier", "Unit Cost", "Total"];
    const rows = purchaseOrder.rows.map((r) => [
      r.item_code, r.item_name, r.quantity, r.unit_of_measure, r.supplier_name ?? "", r.unit_cost.toFixed(2), r.total.toFixed(2),
    ]);
    const csv = [header, ...rows, ["", "", "", "", "", "TOTAL", purchaseOrder.grandTotal.toFixed(2)]]
      .map((row) => row.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `purchase-order-${format(new Date(), "yyyy-MM-dd")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="min-h-screen bg-white" style={{ color: BRAND_DARK }}>
      {/* Header */}
      <div className="border-b border-border bg-white">
        <div className="container py-4 flex items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/dashboard")} aria-label="Back to dashboard">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading text-xl sm:text-2xl font-medium">Purchasing &amp; Stocktake</h1>
              <p className="text-xs sm:text-sm text-muted-foreground">Catalog · Stock counts · Auto-generated POs</p>
            </div>
          </div>
          <Button
            onClick={() => { setEditing(null); setDialogOpen(true); }}
            style={{ backgroundColor: BRAND_GREEN }}
            className="text-white hover:brightness-110 border-0 gap-2"
          >
            <Plus className="w-4 h-4" /> Add item
          </Button>
        </div>
      </div>

      <div className="container py-6 space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <StatCard label="Total items" value={stats.total} />
          <StatCard label="Low stock" value={stats.low} accent={BRAND_ORANGE} />
          <StatCard label="Critical" value={stats.critical} accent="#dc2626" />
          <StatCard label="Inventory value" value={fmtMoney(stats.inventoryValue)} accent={BRAND_GREEN} />
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by code, name, or supplier"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
              maxLength={120}
            />
          </div>
          <Select value={filterCategory} onValueChange={setFilterCategory}>
            <SelectTrigger className="w-full sm:w-48">
              <SelectValue placeholder="All categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="catalog" className="w-full">
          <TabsList className="grid grid-cols-3 w-full sm:w-auto sm:inline-flex">
            <TabsTrigger value="catalog" className="gap-1.5"><Package className="w-3.5 h-3.5" />Catalog</TabsTrigger>
            <TabsTrigger value="stocktake" className="gap-1.5"><ClipboardList className="w-3.5 h-3.5" />Stocktake</TabsTrigger>
            <TabsTrigger value="po" className="gap-1.5">
              <ShoppingCart className="w-3.5 h-3.5" />Purchase Order
              {purchaseOrder.rows.length > 0 && (
                <Badge variant="secondary" className="ml-1 h-4 px-1.5 text-[10px]">{purchaseOrder.rows.length}</Badge>
              )}
            </TabsTrigger>
          </TabsList>

          {/* ─── CATALOG ─── */}
          <TabsContent value="catalog" className="mt-4">
            {loading ? (
              <SkeletonBlock />
            ) : items.length === 0 ? (
              <EmptyState onAdd={() => { setEditing(null); setDialogOpen(true); }} />
            ) : grouped.length === 0 ? (
              <Card className="p-8 text-center text-sm text-muted-foreground">No items match your filters.</Card>
            ) : (
              <div className="space-y-3">
                {grouped.map(([cat, list]) => {
                  const isCollapsed = !!collapsed[cat];
                  return (
                    <Card key={cat} className="overflow-hidden">
                      <button
                        onClick={() => setCollapsed((s) => ({ ...s, [cat]: !s[cat] }))}
                        className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/30 transition-colors"
                      >
                        <div className="flex items-center gap-2">
                          {isCollapsed ? <ChevronRight className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
                          <span className="font-heading font-medium text-sm" style={{ color: BRAND_DARK }}>{cat}</span>
                          <Badge variant="secondary" className="text-[10px]">{list.length}</Badge>
                        </div>
                      </button>
                      {!isCollapsed && (
                        <div className="overflow-x-auto border-t border-border">
                          <table className="w-full text-sm">
                            <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                              <tr>
                                <th className="text-left font-medium px-4 py-2">Code</th>
                                <th className="text-left font-medium px-4 py-2">Item</th>
                                <th className="text-left font-medium px-4 py-2">Unit</th>
                                <th className="text-right font-medium px-4 py-2">Par</th>
                                <th className="text-right font-medium px-4 py-2">Stock</th>
                                <th className="text-left font-medium px-4 py-2">Supplier</th>
                                <th className="text-right font-medium px-4 py-2">Unit cost</th>
                                <th className="text-left font-medium px-4 py-2">Updated</th>
                                <th className="text-right font-medium px-4 py-2">Status</th>
                                <th className="px-4 py-2" />
                              </tr>
                            </thead>
                            <tbody>
                              {list.map((it) => {
                                const status = getStatus(it.current_stock, it.par_level);
                                return (
                                  <tr key={it.id} className="border-t border-border/60 hover:bg-muted/20">
                                    <td className="px-4 py-2.5 font-mono text-xs">{it.item_code}</td>
                                    <td className="px-4 py-2.5">{it.item_name}</td>
                                    <td className="px-4 py-2.5 text-muted-foreground">{it.unit_of_measure}</td>
                                    <td className="px-4 py-2.5 text-right">{Number(it.par_level)}</td>
                                    <td className="px-4 py-2.5 text-right font-medium">{Number(it.current_stock)}</td>
                                    <td className="px-4 py-2.5 text-muted-foreground">{it.supplier_name ?? "—"}</td>
                                    <td className="px-4 py-2.5 text-right">{fmtMoney(Number(it.unit_cost))}</td>
                                    <td className="px-4 py-2.5 text-muted-foreground text-xs">{format(new Date(it.updated_at), "MMM d, yyyy")}</td>
                                    <td className="px-4 py-2.5 text-right"><StatusPill status={status} /></td>
                                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditing(it); setDialogOpen(true); }} aria-label="Edit item">
                                        <Pencil className="w-3.5 h-3.5" />
                                      </Button>
                                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={() => handleDelete(it.id)} aria-label="Delete item">
                                        <Trash2 className="w-3.5 h-3.5" />
                                      </Button>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* ─── STOCKTAKE ─── */}
          <TabsContent value="stocktake" className="mt-4">
            {loading ? <SkeletonBlock /> : items.length === 0 ? (
              <EmptyState onAdd={() => { setEditing(null); setDialogOpen(true); }} />
            ) : filtered.length === 0 ? (
              <Card className="p-8 text-center text-sm text-muted-foreground">No items match your filters.</Card>
            ) : (
              <Card className="overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                      <tr>
                        <th className="text-left font-medium px-4 py-2.5">Code</th>
                        <th className="text-left font-medium px-4 py-2.5">Item Name</th>
                        <th className="text-left font-medium px-4 py-2.5">Unit</th>
                        <th className="text-right font-medium px-4 py-2.5">Par Level</th>
                        <th className="text-right font-medium px-4 py-2.5 w-32">Current Count</th>
                        <th className="text-right font-medium px-4 py-2.5">Variance</th>
                        <th className="text-right font-medium px-4 py-2.5">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((it) => {
                        const status = getStatus(it.current_stock, it.par_level);
                        const variance = Number(it.current_stock) - Number(it.par_level);
                        const varianceColor = variance >= 0 ? BRAND_GREEN : variance >= -Number(it.par_level) * 0.25 ? BRAND_ORANGE : "#dc2626";
                        return (
                          <tr key={it.id} className="border-t border-border/60">
                            <td className="px-4 py-2 font-mono text-xs">{it.item_code}</td>
                            <td className="px-4 py-2">{it.item_name}</td>
                            <td className="px-4 py-2 text-muted-foreground">{it.unit_of_measure}</td>
                            <td className="px-4 py-2 text-right">{Number(it.par_level)}</td>
                            <td className="px-4 py-2 text-right">
                              <div className="relative">
                                <Input
                                  type="number"
                                  min={0}
                                  step="0.01"
                                  defaultValue={String(Number(it.current_stock))}
                                  onBlur={(e) => {
                                    const v = e.target.value;
                                    if (Number(v) !== Number(it.current_stock)) handleStockChange(it.id, v);
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter") (e.target as HTMLInputElement).blur();
                                  }}
                                  className="h-8 text-right pr-7"
                                />
                                {savingCount === it.id && (
                                  <Loader2 className="w-3.5 h-3.5 animate-spin absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-2 text-right font-medium" style={{ color: varianceColor }}>
                              {variance > 0 ? "+" : ""}{variance.toFixed(2)}
                            </td>
                            <td className="px-4 py-2 text-right"><StatusPill status={status} /></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </TabsContent>

          {/* ─── PURCHASE ORDER ─── */}
          <TabsContent value="po" className="mt-4">
            {loading ? <SkeletonBlock /> : purchaseOrder.rows.length === 0 ? (
              <Card className="p-10 text-center">
                <CheckCircle2 className="w-10 h-10 mx-auto mb-3" style={{ color: BRAND_GREEN }} />
                <h3 className="font-heading text-lg font-medium mb-1">All stocked up</h3>
                <p className="text-sm text-muted-foreground">Every item is at or above its par level. No purchase order needed.</p>
              </Card>
            ) : (
              <Card className="overflow-hidden">
                <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" style={{ color: BRAND_ORANGE }} />
                    <span className="font-heading text-sm font-medium">
                      {purchaseOrder.rows.length} item{purchaseOrder.rows.length === 1 ? "" : "s"} below par
                    </span>
                  </div>
                  <Button size="sm" variant="outline" onClick={exportPurchaseOrderCSV} className="gap-1.5">
                    <Download className="w-3.5 h-3.5" /> Export CSV
                  </Button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
                      <tr>
                        <th className="text-left font-medium px-4 py-2.5">Code</th>
                        <th className="text-left font-medium px-4 py-2.5">Item</th>
                        <th className="text-right font-medium px-4 py-2.5">Quantity</th>
                        <th className="text-left font-medium px-4 py-2.5">Unit</th>
                        <th className="text-left font-medium px-4 py-2.5">Supplier</th>
                        <th className="text-right font-medium px-4 py-2.5">Unit cost</th>
                        <th className="text-right font-medium px-4 py-2.5">Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {purchaseOrder.rows.map((r) => (
                        <tr key={r.id} className="border-t border-border/60">
                          <td className="px-4 py-2.5 font-mono text-xs">{r.item_code}</td>
                          <td className="px-4 py-2.5">
                            <div className="flex items-center gap-2">
                              <span>{r.item_name}</span>
                              <StatusPill status={r.status} />
                            </div>
                          </td>
                          <td className="px-4 py-2.5 text-right font-medium">{r.quantity.toFixed(2)}</td>
                          <td className="px-4 py-2.5 text-muted-foreground">{r.unit_of_measure}</td>
                          <td className="px-4 py-2.5 text-muted-foreground">{r.supplier_name ?? "—"}</td>
                          <td className="px-4 py-2.5 text-right">{fmtMoney(r.unit_cost)}</td>
                          <td className="px-4 py-2.5 text-right font-medium">{fmtMoney(r.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="border-t-2" style={{ borderColor: BRAND_GREEN }}>
                        <td colSpan={6} className="px-4 py-3 text-right font-heading font-medium">Total order value</td>
                        <td className="px-4 py-3 text-right font-heading text-lg font-medium" style={{ color: BRAND_GREEN }}>
                          {fmtMoney(purchaseOrder.grandTotal)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>

      <ItemFormDialog open={dialogOpen} onOpenChange={setDialogOpen} initial={editing} onSaved={refresh} />
    </div>
  );
}

// ─── Small helpers ─────────────────────────────────────────────────────
function StatCard({ label, value, accent }: { label: string; value: number | string; accent?: string }) {
  return (
    <Card className="p-4">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground font-medium">{label}</div>
      <div className="font-heading text-2xl font-medium mt-1" style={{ color: accent ?? BRAND_DARK }}>{value}</div>
    </Card>
  );
}

function SkeletonBlock() {
  return (
    <Card className="p-6 space-y-3">
      <div className="h-6 bg-muted rounded animate-pulse w-1/3" />
      <div className="h-4 bg-muted/60 rounded animate-pulse" />
      <div className="h-4 bg-muted/60 rounded animate-pulse" />
      <div className="h-4 bg-muted/60 rounded animate-pulse w-2/3" />
    </Card>
  );
}

function EmptyState({ onAdd }: { onAdd: () => void }) {
  return (
    <Card className="p-10 text-center">
      <Package className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
      <h3 className="font-heading text-lg font-medium mb-1">No items yet</h3>
      <p className="text-sm text-muted-foreground mb-4 max-w-sm mx-auto">
        Build your inventory catalog by adding items with par levels, suppliers and unit costs. We'll auto-generate purchase orders when stock dips below par.
      </p>
      <Button onClick={onAdd} style={{ backgroundColor: BRAND_GREEN }} className="text-white hover:brightness-110 border-0 gap-2">
        <Plus className="w-4 h-4" /> Add your first item
      </Button>
    </Card>
  );
}
